import { useMemo } from "react";
import { useOperator } from "./OperatorContext";

/**
 * Which writes a platform operator may make on each Super Admin screen, from the server gate of
 * every write route the screen calls (route decorators: `require_perm(...)` or
 * `require_super_admin`). Audited route by route on 2026-10-07 (S8, WRITE_PERMS).
 *
 * The write half of screenAccess.js: that table decides whether a screen opens at all (its
 * read); this one decides which of its controls render. A screen reads its row through
 * `useScreenWrites(screen)` and never names a permission itself, so a gate that changes on the
 * server changes here, once. The server still enforces every write; this is what keeps a
 * control the server would refuse off the screen instead of offering it and answering 403.
 *
 * A requirement is one of:
 *   { superAdmin: true }      the route uses `require_super_admin`, which ignores permissions
 *   { perms: ["x.write"] }    `require_perm(...)`; every listed permission is needed
 *
 * A permission requirement is checked against `me.permissions` only, never against
 * `is_super_admin`: a permissions override can revoke a permission from a super admin, and
 * every `require_perm` gate holds them to it (hq-customers-contract.md 3.1).
 */
export const SCREEN_WRITES = {
  // /super-admin/reviews: hide/unhide is tenants.write, the program form PUT is settings.write.
  reviews: {
    moderate: { perms: ["tenants.write"] },
    program: { perms: ["settings.write"] },
  },
  // /super-admin/tenants/{id} (Tenant360): each billing action has its own permission
  // (super_admin_finance_actions.py: billing.rebate, billing.adjust, billing.refund); the trial
  // tile's Extend and End are tenants.write (super_admin_trial_routes.py).
  tenant360: {
    rebate: { perms: ["billing.rebate"] },
    adjustPrice: { perms: ["billing.adjust"] },
    refund: { perms: ["billing.refund"] },
    trial: { perms: ["tenants.write"] },
    // Open practice dashboard: operator_entry.is_practice_operator, a role fact (no permission).
    manageAdmin: { superAdmin: true },
  },
  // /super-admin/tenants (the list): the row's Manage admin entry, same server rule as above.
  tenants: { manageAdmin: { superAdmin: true } },
  // /super-admin/custom-domains: Re-check and Remove are tenants.write; the grace-period PUT
  // is settings.write.
  customDomains: {
    manage: { perms: ["tenants.write"] },
    policy: { perms: ["settings.write"] },
  },
  // /super-admin/team: invite, edit, remove.
  team: { manage: { perms: ["team.write"] } },
  // /super-admin/gateway: refunds, disputes, commission rules, settings, adjustments, consult
  // pricing. The step-up mint (POST /gateway/reauth) is finance.write as well.
  gateway: { manage: { perms: ["finance.write"] } },
  // /super-admin/affiliate: program, rules, affiliate status, reversals (all step-up, minted
  // with finance.write).
  affiliate: { manage: { perms: ["finance.write"] } },
  // /super-admin/payments: PUT /payments/providers and its test are settings.write; the blocked
  // payment methods (GET and PUT /payment-methods) are super admin only.
  payments: {
    providers: { perms: ["settings.write"] },
    methodPolicy: { superAdmin: true },
  },
  // /super-admin/hq/customers: create, update, delete, communications, sync.
  hqCustomers: { manage: { perms: ["data.crm.write"] } },
  // /super-admin/hq/finance: add and delete an expense.
  hqFinance: { expenses: { perms: ["finance.write"] } },
  // /super-admin/hq/service-leads: the status PUT.
  serviceLeads: { manage: { perms: ["data.crm.write"] } },
  // /super-admin/hq/social: connect/disconnect accounts, create/delete posts.
  social: { manage: { perms: ["settings.write"] } },
  // /super-admin/hq/gift-promo: promos (create, edit, toggle) and gift cards (issue, resend,
  // void). The promo form's plan picker reads GET /plans, which is super admin only.
  giftPromo: {
    manage: { perms: ["settings.write"] },
    planCatalogue: { superAdmin: true },
  },
  // /super-admin/hq/ai: generating an insight and chatting with the agent store rows and spend
  // a model call (contract 3.2).
  ai: { generate: { perms: ["data.insights.write"] } },
  // /super-admin/hq/automation. The lifecycle policy PUT is settings.write behind a step-up
  // whose mint needs finance.write, so saving it takes both.
  automation: {
    rules: { perms: ["settings.write"] },
    lifecyclePolicy: { perms: ["settings.write", "finance.write"] },
    lifecycleRun: { perms: ["settings.write"] },
  },
  // /super-admin/hq/blog: create, update, publish, delete.
  blog: { manage: { perms: ["settings.write"] } },
  // /super-admin/hq/site: content overrides, media, links.
  site: { manage: { perms: ["settings.write"] } },
  // /super-admin/integrations: save and remove stored values.
  integrations: { manage: { perms: ["settings.write"] } },
};

/** Whether `operator` (`useOperator()`'s `{ isSuperAdmin, can }`) meets `requirement`. */
export function meets(requirement, operator) {
  if (!requirement) return false;
  if (requirement.superAdmin) return operator.isSuperAdmin === true;
  const perms = Array.isArray(requirement.perms) ? requirement.perms : [];
  return perms.length > 0 && perms.every((p) => operator.can(p));
}

/**
 * `{ [write]: bool }` for one row of SCREEN_WRITES, for the signed-in operator. Only valid
 * under `<OperatorProvider>`.
 */
export function useScreenWrites(screen) {
  const operator = useOperator();
  const row = SCREEN_WRITES[screen];
  if (!row) throw new Error(`useScreenWrites(): no SCREEN_WRITES row named ${JSON.stringify(screen)}`);
  return useMemo(
    () => Object.fromEntries(Object.entries(row).map(([write, requirement]) => [write, meets(requirement, operator)])),
    [row, operator],
  );
}
